import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { SquircleDefs } from "@/components/shared/Logotype";
import { QueueProvider } from "@/components/QueueContext";
import Auth from "@/pages/Auth";
import Shell from "@/components/Shell";
import Dashboard from "@/pages/Dashboard";
import Giraffe from "@/pages/Giraffe";
import GiraffeCountries from "@/pages/GiraffeCountries";
import GiraffeTrip from "@/pages/GiraffeTrip";
import GiraffeSettings from "@/pages/GiraffeSettings";
import GiraffeMemoir from "@/pages/GiraffeMemoir";
import GiraffeMemoirPrint from "@/pages/GiraffeMemoirPrint";
import Framed from "@/pages/Framed";
import FramedCollection from "@/pages/FramedCollection";
import FramedGallery from "@/pages/FramedGallery";
import CuratedGallery from "@/pages/CuratedGallery";
import CultOfOne from "@/pages/CultOfOne";
import Uncode from "@/pages/Uncode";
import Zephyr from "@/pages/Zephyr";
import Dash from "@/pages/Dash";
import SoulSync from "@/pages/SoulSync";
import SoulSyncCodexArchive from "@/pages/SoulSyncCodexArchive";
import SoulSyncLibraryArchive from "@/pages/SoulSyncLibraryArchive";
import ArxActa from "@/pages/ArxActa";
import Library from "@/pages/Library";
import Labs from "@/pages/Labs";
import AppLogoPreview from "@/pages/AppLogoPreview";
import AdminArchitecture from "@/pages/AdminArchitecture";
import AdminAIContextDocument from "@/pages/AdminAIContextDocument";
import Verve from "@/pages/Verve";
import Flux from "@/pages/Flux";
import Xent from "@/pages/Xent";
import ErrorLog from "@/pages/ErrorLog";
import CultGallery from "@/pages/CultGallery";
import CuratedEditor from "@/pages/CuratedEditor";
import CuratedPrint from "@/pages/CuratedPrint";
import CapeTownPublic from "@/pages/public/CapeTownPublic";
import TripMemoir from "@/pages/public/TripMemoir";
import TripView from "@/pages/public/TripView";
import TravelsGallery from "@/pages/public/TravelsGallery";
import TravelsCountries from "@/pages/public/TravelsCountries";
import TravelsPostcards from "@/pages/public/TravelsPostcards";
import TravelsPostcard from "@/pages/public/TravelsPostcard";
import LabsGallery from "@/pages/public/LabsGallery";

// Keep-alive ping to prevent backend cold starts
const KEEP_ALIVE_INTERVAL = 4 * 60 * 1000; // 4 minutes

// Reload after 5 minutes in the background so data is never stale on resume
const BACKGROUND_RELOAD_THRESHOLD = 5 * 60 * 1000; // 5 minutes

function useBackgroundReload() {
  useEffect(() => {
    let hiddenAt = null;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
      } else if (document.visibilityState === 'visible' && hiddenAt !== null) {
        if (Date.now() - hiddenAt > BACKGROUND_RELOAD_THRESHOLD) {
          window.location.reload();
        }
        hiddenAt = null;
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);
}

function useKeepAlive() {
  useEffect(() => {
    const ping = () => {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/health`, { method: 'GET' })
        .catch(() => {}); // Silently ignore errors
    };
    
    // Initial ping
    ping();
    
    // Set up interval
    const interval = setInterval(ping, KEEP_ALIVE_INTERVAL);
    
    return () => clearInterval(interval);
  }, []);
}

const ROUTE_TITLES = {
  '/dashboard': 'Dashboard',
  '/uncode': 'Uncode',
  '/giraffe': 'Giraffe',
  '/verve': 'Verve',
  '/soulsync': 'SoulSync',
  '/dash': 'Dash',
  '/cult': 'Cult of One',
  '/framed': 'Framed',
  '/arx': 'Arx Acta',
  '/xent': 'xeNT',
  '/zephyr': 'Zephyr',
  '/library': 'Library',
  '/flux': 'Flux',
  '/auth': 'Sign In',
};

function TitleUpdater() {
  const { pathname } = useLocation();
  useEffect(() => {
    const match = Object.keys(ROUTE_TITLES).find(r => pathname === r || pathname.startsWith(r + '/'));
    document.title = match ? `Zephyr OS — ${ROUTE_TITLES[match]}` : 'Zephyr OS';
  }, [pathname]);
  return null;
}

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  if (loading) return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
      <div className="w-8 h-8 rounded-lg bg-[#6B35B8] animate-pulse" />
    </div>
  );
  if (!isAuthenticated) {
    const from = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth?from=${from}`} replace />;
  }
  return children;
}

function App() {
  // Keep backend warm to prevent cold start delays
  useKeepAlive();
  // Reload stale state after returning from background
  useBackgroundReload();

  const hostname = window.location.hostname;
  const isUncode = hostname.startsWith('uncode.');
  const isGiraffe = hostname.startsWith('giraffe.');
  const isZephyr = hostname.startsWith('zephyr.');
  const isVerve = hostname.startsWith('verve.');
  const isFlux = hostname.startsWith('flux.');

  if (isUncode) {
    return (
      <AuthProvider>
        <BrowserRouter>
          <SquircleDefs />
          <TitleUpdater />
          <Routes>
            <Route path="/auth" element={<Auth />} />
            <Route path="/" element={
              <ProtectedRoute>
                <Uncode />
              </ProtectedRoute>
            } />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    );
  }

  if (isGiraffe) {
    return (
      <AuthProvider>
        <BrowserRouter>
          <SquircleDefs />
          <TitleUpdater />
          <Routes>
            <Route path="/auth" element={<Auth />} />
            <Route path="/" element={<ProtectedRoute><Giraffe /></ProtectedRoute>} />
            <Route path="/countries" element={<ProtectedRoute><GiraffeCountries /></ProtectedRoute>} />
            <Route path="/memoir/:slug" element={<GiraffeMemoir />} />
            <Route path="/memoir/:slug/print" element={<GiraffeMemoirPrint />} />
            <Route path="/trip/:slug" element={<TripView />} />
            <Route path="/p/:slug" element={<TravelsPostcard />} />
            <Route path="/:tripId" element={<ProtectedRoute><GiraffeTrip /></ProtectedRoute>} />
            <Route path="/:tripId/settings" element={<ProtectedRoute><GiraffeSettings /></ProtectedRoute>} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    );
  }

  if (isZephyr) {
    return (
      <AuthProvider>
        <BrowserRouter>
          <SquircleDefs />
          <TitleUpdater />
          <Routes>
            <Route path="/auth" element={<Auth />} />
            <Route path="/" element={
              <ProtectedRoute>
                <Zephyr />
              </ProtectedRoute>
            } />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    );
  }

  if (isFlux) {
    return (
      <AuthProvider>
        <BrowserRouter>
          <SquircleDefs />
          <TitleUpdater />
          <Routes>
            <Route path="/auth" element={<Auth />} />
            <Route path="/flux" element={<Navigate to="/" replace />} />
            <Route path="/" element={
              <ProtectedRoute>
                <Flux />
              </ProtectedRoute>
            } />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    );
  }

  if (isVerve) {
    return (
      <AuthProvider>
        <BrowserRouter>
          <SquircleDefs />
          <TitleUpdater />
          <Routes>
            <Route path="/auth" element={<Auth />} />
            <Route path="/" element={
              <ProtectedRoute>
                <Verve />
              </ProtectedRoute>
            } />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    );
  }

  return (
    <AuthProvider>
      <QueueProvider>
      <BrowserRouter>
        <SquircleDefs />
        <TitleUpdater />
        <Routes>
          <Route path="/auth" element={<Auth />} />
          <Route path="/labs" element={<Navigate to="/gallery" replace />} />
          <Route path="/applogopreview" element={<AppLogoPreview />} />
          <Route path="/gallery" element={<Labs />} />
          <Route path="/gallery/framed" element={<FramedGallery />} />
          <Route path="/gallery/labs" element={<LabsGallery />} />
          <Route path="/gallery/cult" element={<CultGallery />} />
          <Route path="/gallery/cult/curated" element={<Navigate to="/gallery/cult?tab=curated" replace />} />
          <Route path="/gallery/cult/curated/:slug" element={<CapeTownPublic />} />
          <Route path="/capetown" element={<Navigate to="/gallery/cult/curated/capetown" replace />} />
          <Route path="/gallery/curated/:collectionId" element={<CuratedGallery />} />
          <Route path="/curated/:slug/print" element={<CuratedPrint />} />
          <Route path="/giraffe/memoir/:slug" element={<GiraffeMemoir />} />
          <Route path="/giraffe/memoir/:slug/print" element={<GiraffeMemoirPrint />} />
          <Route path="/trip/:slug/memoir" element={<TripMemoir />} />
          <Route path="/trip/:slug" element={<TripView />} />
          <Route path="/travels" element={<TravelsGallery />} />
          <Route path="/travels/countries" element={<TravelsCountries />} />
          <Route path="/travels/postcards" element={<TravelsPostcards />} />
          <Route path="/gallery/postcard/:slug" element={<TravelsPostcard />} />
          <Route path="/travels/postcard/:slug" element={<TravelsPostcard />} />
          <Route path="/p/:slug" element={<TravelsPostcard />} />
          <Route path="/framed/collection/:slug" element={<FramedCollection />} />
          <Route path="/" element={
            <ProtectedRoute><Shell /></ProtectedRoute>
          }>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="giraffe" element={<Giraffe />} />
            <Route path="giraffe/countries" element={<GiraffeCountries />} />
            <Route path="giraffe/:tripId" element={<GiraffeTrip />} />
            <Route path="giraffe/:tripId/settings" element={<GiraffeSettings />} />
            <Route path="framed" element={<Framed />} />
            <Route path="cult" element={<CultOfOne />} />
            <Route path="curated/:slug" element={<CuratedEditor />} />
            <Route path="uncode" element={<Uncode />} />
            <Route path="dash" element={<Dash />} />
            <Route path="soulsync" element={<SoulSync />} />
            <Route path="soulsync/codex/archive" element={<SoulSyncCodexArchive />} />
            <Route path="soulsync/library/archive" element={<SoulSyncLibraryArchive />} />
            <Route path="arx" element={<ArxActa />} />
            <Route path="library" element={<Library />} />
            <Route path="zephyr" element={<Zephyr />} />
            <Route path="admin/architecture" element={<AdminArchitecture />} />
            <Route path="admin/ai-context/:slug" element={<AdminAIContextDocument />} />
            <Route path="verve" element={<Verve />} />
            <Route path="flux" element={<Flux />} />
            <Route path="xent" element={<Xent />} />
            <Route path="errors" element={<ErrorLog />} />
          </Route>
        </Routes>
      </BrowserRouter>
      </QueueProvider>
    </AuthProvider>
  );
}

export default App;
